import { ColoredCircle, EllipsedText, FlexColumn, FlexRow, LabeledData } from '@gorila-shared-ui/components';
import { LabelMedium, ParagraphSmall } from 'baseui/typography';
import { memo } from 'react';
import { useStyles } from '../../hooks/useStyles';
import { Vehicle } from '../../types/vehicle';
import { isBrand, isSubBrand } from '../../utils/currentStates';
import { getFormattedNumber } from '../../utils/numbers';
import { capitalize } from '../../utils/strings';
import VehicleIcon from '../map/markers/VehicleIcon';
import { StyledTooltip } from '../ui/StyledTooltip';

type Props = {
  vehicle?: Vehicle;
  odometer?: number;
  hourometer?: number;
  simpleMode?: boolean;
  ident?: string;
  dataOnly?: boolean;
  hiddenUnit?: boolean;
  hiddenOdometer?: boolean;
  hiddenHourometer?: boolean;
};

function VehicleData({
  vehicle,
  odometer,
  hourometer,
  simpleMode = false,
  ident,
  dataOnly = false,
  hiddenUnit = false,
  hiddenHourometer = false,
  hiddenOdometer = false,
}: Readonly<Props>) {
  const { theme, classes, css } = useStyles();
  const brandName = isBrand(vehicle?.brand) ? vehicle.brand.name : vehicle?.brand;
  const subBrandName = isSubBrand(vehicle?.subBrand) ? vehicle.subBrand.name : vehicle?.subBrand;
  const labelWidth = simpleMode ? '90px' : undefined;
  const unitName =
    vehicle && brandName && subBrandName
      ? `${brandName} ${subBrandName} ${vehicle.year}, ${vehicle.licensePlate}`.trim()
      : vehicle?.licensePlate || '-';

  return (
    <FlexColumn gap={theme.sizing.scale200}>
      {!dataOnly && <LabelMedium className={classes.dataHeaderClass}>Datos del vehículo</LabelMedium>}
      <FlexColumn
        gap={theme.sizing.scale200}
        classNames={!dataOnly ? classes.dataContentClass : undefined}
      >
        {simpleMode && !hiddenUnit && (
          <LabeledData
            label="Unidad:"
            labelWidth={labelWidth}
            content={
              <ParagraphSmall
                color={theme.colors.contentSecondary}
                margin={0}
                className={`${classes.horizontalFlex} ${css({
                  gap: theme.sizing.scale200,
                  alignItems: 'center',
                })}`}
              >
                <EllipsedText
                  breakAll
                  maxLines={2}
                >
                  {unitName}
                </EllipsedText>
                {vehicle?.color && (
                  <span style={{ flexShrink: 0, lineHeight: 0 }}>
                    <ColoredCircle color={vehicle.color} />
                  </span>
                )}
              </ParagraphSmall>
            }
          />
        )}
        {!simpleMode && !hiddenUnit && (
          <>
            <LabeledData
              label="Placa:"
              content={vehicle?.licensePlate || '-'}
            />
            <LabeledData
              label="Tipo:"
              verticalAlign="center"
              content={
                vehicle?.vehicleType ? (
                  <FlexRow
                    centered
                    gap={theme.sizing.scale200}
                  >
                    <StyledTooltip
                      content={capitalize(vehicle.vehicleType)}
                      showArrow={false}
                      placement="top"
                      ignoreBoundary
                      contentStyle={{
                        transform: 'rotate(90deg)',
                      }}
                    >
                      <VehicleIcon
                        vehicleType={vehicle.vehicleType}
                        color={vehicle?.color}
                        iconSize={36}
                      />
                    </StyledTooltip>
                    {vehicle?.color && <ColoredCircle color={vehicle.color} />}
                  </FlexRow>
                ) : (
                  '-'
                )
              }
            />
            <LabeledData
              label="Marca:"
              content={brandName || '-'}
            />
            <LabeledData
              label="Submarca:"
              content={subBrandName || '-'}
            />
            <LabeledData
              label="Modelo:"
              content={vehicle?.year?.toString()}
            />
          </>
        )}
        <LabeledData
          label="No. Serie:"
          content={vehicle?.vin}
          labelWidth={labelWidth}
        />
        {ident && (
          <LabeledData
            label="Ident:"
            content={ident}
            labelWidth={labelWidth}
          />
        )}
        {!hiddenOdometer && odometer !== undefined && (
          <LabeledData
            label={'Odómetro:'}
            content={getFormattedNumber(odometer, { suffix: 'km' })}
            labelWidth={labelWidth}
          />
        )}
        {!hiddenHourometer && hourometer !== undefined && (
          <LabeledData
            label={'Horómetro:'}
            content={getFormattedNumber(hourometer, { suffix: 'hrs' })}
            labelWidth={labelWidth}
          />
        )}
      </FlexColumn>
    </FlexColumn>
  );
}

export default memo(VehicleData);
